import { useQuery } from "react-query";
import { useUser } from "@/providers/context";
import { getFavourites, ItemType } from "@/api";
import { useCallback, useMemo } from "react";
import { ITEM_TYPE_COLLECTION, ITEM_TYPE_VIDEO } from "@/const.ts";
import { useAppProperties } from "@/hooks/useAppProperties.tsx";

export const useFavourites = () => {
  const { uid } = useUser();
  const { showFavourites } = useAppProperties();
  const query = useQuery(
    ["favourites", uid],
    () => {
      if (uid) {
        return getFavourites({ user_id: uid });
      }
    },
    {
      enabled: showFavourites,
    },
  );

  const favouriteData = useMemo(() => {
    if (!query.data) {
      return query.data;
    }
    const { Favourites } = { ...query.data };
    return { ...query.data, Watchlist: Favourites.sort((a, b) => b.Id - a.Id) };
  }, [query.data]);

  const { collections, videos } = useMemo(() => {
    const videos = new Set<number>();
    const collections = new Set<number>();
    const favourites = query.data?.Favourites ?? [];
    for (const f of favourites) {
      if (isNaN(Number(f.ItemId))) {
        continue; // skip unexpected item ids
      }
      if (f.ItemType == ITEM_TYPE_VIDEO) {
        videos.add(parseInt(f.ItemId));
      }
      if (f.ItemType == ITEM_TYPE_COLLECTION) {
        collections.add(parseInt(f.ItemId));
      }
    }

    return { videos, collections };
  }, [query.data]);

  const isFavourited = useCallback(
    ({ id, type }: { id: number; type: ItemType }) => {
      if (type === ITEM_TYPE_VIDEO) {
        return videos.has(id);
      } else if (type === ITEM_TYPE_COLLECTION) {
        return collections.has(id);
      }
      return false;
    },
    [videos, collections],
  );

  return { ...query, data: favouriteData, isFavourited };
};
